import React from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  .api-title {
    background: linear-gradient(110deg, #fbbf24 35%, #a78bfa 50%, #fbbf24 65%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: api-shimmer 8s linear infinite;
  }

  @keyframes api-shimmer {
    0%   { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }

  .api-dot-grid {
    background-image: radial-gradient(circle, rgba(251,191,36,0.12) 1px, transparent 1px);
    background-size: 28px 28px;
  }

  @keyframes api-fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .api-fade { animation: api-fadeUp 0.7s ease forwards; }
  .api-fade-1 { animation-delay: 0.05s; opacity: 0; }
  .api-fade-2 { animation-delay: 0.15s; opacity: 0; }

  @keyframes api-float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-15px) rotate(5deg); }
  }
  .api-float {
    animation: api-float 4s ease-in-out infinite;
  }

  .api-btn {
    position: relative; overflow: hidden;
    transition: all 0.3s ease;
  }
  .api-btn::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  .api-btn:hover::after { transform: translateX(100%); }
  .api-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(217,119,6,0.35);
    background-color: #d97706;
  }
`;

const Icon = ({ d, size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
    strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);

const ICONS = {
  whatsapp: "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z",
  code: "M16 18l6-6-6-6M8 6l-6 6 6 6",
};

const APIReferencePage = ({ setCurrentPage = () => {} }) => {
  return (
    <>
      <style>{styles}</style>
      <div className="space-y-20 max-w-6xl mx-auto px-4 pb-20">
        
          {/* ── HERO ─────────────────────────────────────────── */}
          <section className="relative pt-16 pb-10 text-center api-fade api-fade-1">
            <div className="api-dot-grid absolute inset-0 rounded-2xl -z-10 opacity-60" />
            
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-600/30 
              bg-amber-900/10 mb-6">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">
                COMING SOON
              </span>
            </div>

            <div className="mb-8 api-float">
              <Icon d={ICONS.code} size={80} className="text-violet-400/30 mx-auto" />
            </div>

            <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-5">
              <span className="api-title">API Reference</span>
            </h1>
            
            <p className="text-slate-400 max-w-xl mx-auto text-base leading-relaxed">
              Complete technical specifications, endpoints, authentication methods, and code 
              examples for seamless integration with HEKAOS APIs.
            </p>
          </section>

          {/* ── FEATURES PREVIEW ────────────────────────────── */}
          <section className="mb-32 api-fade api-fade-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: "Authentication", desc: "API keys, OAuth flows, and security best practices" },
                { title: "Endpoints", desc: "Complete REST API reference with request/response examples" },
                { title: "SDKs & Libraries", desc: "Official client libraries for Python, Node.js, and more" }
              ].map((item, i) => (
                <div key={i} 
                  className="rounded-2xl border border-violet-800/30 bg-gradient-to-br from-violet-900/10 to-slate-900/50 
                    p-6 backdrop-blur-sm hover:border-violet-700/50 transition-all duration-300">
                  <h3 className="text-violet-400 font-bold text-lg mb-2 tracking-tight">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* ── CTA SECTION ─────────────────────────────────── */}
          <section className="api-fade api-fade-2 text-center p-12 rounded-2xl bg-gradient-to-br from-amber-900/20 to-violet-900/20 border border-amber-800/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-amber-400 mb-4">
              Ready to Integrate?
            </h2>
            <p className="text-violet-300 mb-8 max-w-2xl mx-auto">
              Talk to our engineering team about API access, authentication, rate limits, 
              and custom integrations for your use case.
            </p>
            
            <a href="https://wa.me/919235327048?text=Hi%2C%20I%20need%20help%20with%20HEKAOS%20API%20integration" 
              target="_blank" rel="noopener noreferrer"
              className="api-btn inline-flex items-center gap-2 bg-amber-600 text-black px-8 py-3 rounded-lg font-bold no-underline text-sm">
              <Icon d={ICONS.whatsapp} size={16} />
              Chat With Our Team
            </a>
          </section>

        </div>
    </>
  );
};

export default APIReferencePage;